import NewsletterCard from "../../components/cards/NewsletterCard";
import { Carousel } from "../../components/carousel";
import { CarouselItem } from "../../components/carousel/sub-components/item";
import { cn } from "../../../src/utils/shadcn";
import { Container } from "../../components/container";
import type { BlogApiResponse, BlogCard } from "../../types/blogs";
import { getBlogsReq } from "../../utils/api";
import { useEffect, useState } from "react";

const btnPrevNextClasses = cn(
  " text-primary-900 border border-primary-900 hover:text-primary hover:border-primary ",
);

const NewsletterSection = ({
  title,
  textColor = "#232323",
  backgroundImageSrc,
  isWhite = false,
}: {
  title: string;
  textColor?: string;
  backgroundImageSrc?: string;
  isWhite?: boolean;
}) => {
  const [transformedBlogs, setTransformedBlogs] = useState<BlogCard[]>([]);

  async function getBlogs() {
    try {
      const blogsResponse: BlogApiResponse = await getBlogsReq();
      if (blogsResponse && blogsResponse.results) {
        // Take only the first 4 items from the results array
        const firstFourBlogs = blogsResponse.results.slice(0, 4);

        setTransformedBlogs(
          firstFourBlogs.map(
            (blog): BlogCard => ({
              title: blog.heading,
              image: blog.logo,
              button: "Blog",
              slug: blog.slug,
              date: new Date(blog.created_at).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              }),
              description: blog.short_description,
              icon: blog.author.profile_img || "/images/default-img.png",
              author: `${blog.author.first_name} ${blog.author.last_name}`,
            }),
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
    }
  }

  useEffect(() => {
    getBlogs();
  }, []);

  return (
    <div className="relative bg-cover py-5 max-w-6xl mx-auto">
      {/* Background Images */}
      {backgroundImageSrc && (
        <div className="absolute inset-0 w-full h-full">
          <img
            src={backgroundImageSrc}
            alt="Overlay"
            className="absolute bottom-0 right-0  max-w-[50%] max-h-[50%]  object-cover"
          />
        </div>
      )}

      {/* Content Section */}
      <Container isFluid>
        <h1
          className={`my-8 md:my-16 font-bold text-lg leading-[28px] md:text-xl md:leading-[46px] ${textColor}`}
        >
          {title}
        </h1>

        {/* Render the Carousel only if there are cards */}
        {transformedBlogs.length > 0 && (
          <Carousel
            itemsPerSlide={{
              initial: 1,
              sm: 1,
              md: 2,
              lg: 3,
              xl: 3,
              "2xl": 3,
            }}
            itemGap={{
              initial: 24,
              sm: 24,
              md: 24,
              lg: 24,
              xl: 24,
              "2xl": 24,
            }}
            hasNavigation
            hasPagination
            haveOffset={false}
            navigationPrevBtnClassName={btnPrevNextClasses}
            navigationNextBtnClassName={btnPrevNextClasses}
            paginationBulletClassName="bg-[#EEEEFF]"
            loop
            hasMobileNavigation
            autoPlay={{ interval: 3000, pauseOnHover: true }}
            isWhite={isWhite}
          >
            {transformedBlogs.map((card, index) => (
              <CarouselItem key={index}>
                <NewsletterCard card={card} slug={card.slug} index={index} />
              </CarouselItem>
            ))}
          </Carousel>
        )}
      </Container>
    </div>
  );
};

export default NewsletterSection;
